import { useEffect } from "react";
import { useDispatch } from 'react-redux';
import { sharedActions, featuresActions } from '../../../store';
import { router } from '../../../App';
import styles from './ChamProjects.module.css';

// Base component 
export default function ChamProjects({ children }) {
    const dispatch = useDispatch();

    useEffect(() => {
        const chamProjectsEl = document.querySelector('cham-projects');
        if (!chamProjectsEl) return;

        const handleGotoViz = function (e) {
            const shortCode = e.value.aux2;
            window.IMWC.lizard.getShortenerData(shortCode).then(function (response) {
                const data = JSON.parse(response.data.data1);
                document.querySelector('cham-projects').setAttribute("active", "0");
                
                // Set flag to indicate we're restoring from a saved project
                // This prevents the Visualizer from running its auto-restoration logic
                dispatch(sharedActions.setIsRestoringProject(true));
                
                // Restore shared state
                if (data.menuActiveTab) {
                    dispatch(sharedActions.setMenuActiveTab(data.menuActiveTab));
                }
                if (data.selectedFloorPlanData) {
                    dispatch(sharedActions.setSelectedFloorPlanData(data.selectedFloorPlanData));
                    dispatch(sharedActions.setRoomscene(data.selectedFloorPlanData.specifier));
                    dispatch(sharedActions.setCurrentView('room'));
                }
                
                // Restore features state
                if (data.selectedInteriorFloorPlan) {
                    dispatch(featuresActions.setSelectedInteriorFloorPlan(data.selectedInteriorFloorPlan));
                }
                if (data.floorPlanInfo) {
                    dispatch(featuresActions.setFloorPlanInfo(data.floorPlanInfo));
                }
                if (data.selectedSurfaces) {
                    Object.entries(data.selectedSurfaces).forEach(([tab, surfaceId]) => {
                        if (surfaceId) {
                            dispatch(featuresActions.setSelectedSurfaceForTab({ tab, surfaceId }));
                        }
                    });
                }
                if (data.exteriorAppliedProducts) {
                    Object.entries(data.exteriorAppliedProducts).forEach(([surface, product]) => {
                        dispatch(featuresActions.setAppliedProduct({ tab: 'exterior', surface, product }));
                    });
                }
                if (data.kitchenAppliedProducts) {
                    Object.entries(data.kitchenAppliedProducts).forEach(([surface, product]) => {
                        dispatch(featuresActions.setAppliedProduct({ tab: 'kitchen', surface, product }));
                    });
                }
                if (data.selectedPaintPackage) {
                    dispatch(featuresActions.setSelectedPaintPackage(data.selectedPaintPackage));
                }
                if (data.vizDataByTab) {
                    Object.entries(data.vizDataByTab).forEach(([tab, vizData]) => {
                        dispatch(featuresActions.setVizDataForTab({ tab, ...vizData }));
                    });
                }
                
                // Navigate based on the saved active tab
                const activeTab = data.menuActiveTab || 'exterior';
                const vizData = data.vizDataByTab?.[activeTab];
                
                if (vizData?.specifier) {
                    router.navigate({
                        pathname: `/visualizer/spec/${encodeURIComponent(vizData.specifier)}/sel/${vizData.sel || '-1'}`,
                        search: window.location.search,
                        hash: window.location.hash,
                    });
                } else if (data.selectedFloorPlanData?.specifier) {
                    // Fallback to selectedFloorPlanData if vizDataByTab not available
                    router.navigate({
                        pathname: `/visualizer/spec/${encodeURIComponent(data.selectedFloorPlanData.specifier)}/sel/-1`,
                        search: window.location.search,
                        hash: window.location.hash,
                    });
                } else {
                    // Last resort fallback
                    router.navigate('/visualizer/');
                }
                
                // Clear the restoration flag after a short delay to allow the visualizer to mount
                setTimeout(() => {
                    dispatch(sharedActions.setIsRestoringProject(false));
                }, 500);
            }).catch(function (err) {
                console.error('Error loading project:', err);
                dispatch(sharedActions.setIsRestoringProject(false));
                alert('An unknown error occurred.');
            });
        };

        chamProjectsEl.addEventListener('gotoviz', handleGotoViz);
        
        return () => {
            chamProjectsEl.removeEventListener('gotoviz', handleGotoViz);
        };
    }, [dispatch]);

    return (
        <cham-projects base64thumb="1" className={styles.chamProjects}>{children}</cham-projects>
    );
}

// Open projects dialog 
export function ChamProjectsOpenButton({ className, title }) {
    const handleClick = function () {
        document.querySelector('cham-projects').setAttribute("active", "1");
    };

    return (
        <button onClick={handleClick} className={className}>{title}</button>
    );
}

// Saves a project - this is now handled by TopMenu.handleSaveClick()
// This button can be used as a trigger, but the actual save logic should be in the parent component
export function ChamProjectsSaveButton({ className, title, onSave }) {
    const handleClick = function () {
        if (onSave) {
            onSave();
        }
    };

    return (
        <button onClick={handleClick} className={className}>{title}</button>
    );
}
